import { useState, useContext } from "react";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { AuthContext } from "../../../../../context/AuthContext";
import { isProjectManager } from "../../../../../utils/constants/accountType";

const TYPE_BADGE = {
  "In-House": "bg-sky-50 text-sky-700",
  Freelance: "bg-emerald-50 text-emerald-700",
  Company: "bg-amber-50 text-amber-700",
};

const TypeBadge = ({ type }) => {
  if (!type) return <span className="text-gray-500">—</span>;
  const cls = TYPE_BADGE[type] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>
      {type}
    </span>
  );
};

const Pill = ({ enabled }) => (
  <span
    className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${
      enabled ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {enabled ? "Enabled" : "Disabled"}
  </span>
);

const SectionHeader = ({ title, children }) => (
  <div className="flex justify-between items-center mb-3">
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
      {title}
    </h3>
    {children && <div className="flex items-center gap-2 text-sm">{children}</div>}
  </div>
);

const Card = ({ children }) => (
  <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
    {children}
  </div>
);

const btnPrimary =
  "px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded";
const btnSecondary =
  "px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded";
const btnDanger =
  "px-3 py-1.5 font-medium text-white bg-red-600 hover:bg-red-700 rounded";
const btnWarning =
  "px-3 py-1.5 font-medium text-white bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 disabled:cursor-not-allowed rounded";

const VendorView = ({
  vendor,
  onEditGeneral,
  onEditPrimary,
  onEditSettings,
  onDelete,
  onResendInvitation,
  resendingInvitation = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const readOnly = isProjectManager(user);

  const handleConfirmDelete = () => {
    setIsModalOpen(false);
    onDelete();
  };

  const primaryUser = vendor.primary_users || {};
  const showResend = vendor.can_login && !vendor.auth?.is_active;

  return (
    <div className="space-y-5">
      <section>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-bold text-gray-900">General Info</h2>

          <div className="flex items-center gap-2 text-sm">
            <button className={btnSecondary}>Quick Links ▾</button>
            {!readOnly && (
              <>
                <button onClick={onEditGeneral} className={btnPrimary}>
                  Update
                </button>
                <button onClick={() => setIsModalOpen(true)} className={btnDanger}>
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        <Card>
          <div className="space-y-3">
            <Field label="Type" value={<TypeBadge type={vendor.type} />} />
            <Field label="Company Name" value={vendor.company_name} />
            <Field label="Legal Entity" value={vendor.legal_entity} />
            <Field label="PAN / Tax Number" value={vendor.pan_tax_number} />
            <Field label="GSTIN / VAT Number" value={vendor.gstin_vat_number} />
            <Field
              label="Website"
              value={
                vendor.website ? (
                  <a
                    href={vendor.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {vendor.website}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            <Field label="Note" value={vendor.note} />
            <Field
              label="Assignable to Jobs"
              value={<Pill enabled={vendor.assignable_to_jobs} />}
            />
            <Field
              label="Finances Visible"
              value={<Pill enabled={vendor.finances_visible} />}
            />
          </div>
          <div className="space-y-3">
            <Field label="Country" value={vendor.country} />
            <Field label="State / Region" value={vendor.state_region} />
            <Field label="City" value={vendor.city} />
            <Field label="Address" value={vendor.address} />
            <Field label="Postal Code" value={vendor.postal_code} />
            <Field label="Tags" value={vendor.tag} />
            <Field label="Rating" value={vendor.rating} />
            <Field
              label="Created At"
              value={
                vendor.createdAt
                  ? new Date(vendor.createdAt).toLocaleString()
                  : "—"
              }
            />
            <Field
              label="Registered At"
              value={
                vendor.registeredAt
                  ? new Date(vendor.registeredAt).toLocaleString()
                  : "—"
              }
            />
          </div>
        </Card>
      </section>

      <section>
        <SectionHeader title="Primary User Details">
          {!readOnly && showResend && (
            <button
              onClick={onResendInvitation}
              disabled={resendingInvitation}
              className={btnWarning}
            >
              {resendingInvitation ? "Sending..." : "Resend Invitation"}
            </button>
          )}
          {!readOnly && (
            <button onClick={onEditPrimary} className={btnPrimary}>
              Update
            </button>
          )}
        </SectionHeader>

        <Card>
          <div className="space-y-3">
            <Field
              label="Name"
              value={`${primaryUser.first_name || ""} ${
                primaryUser.last_name || ""
              }`.trim()}
            />
            <Field label="Email" value={vendor.auth?.email} />
            <Field label="Timezone" value={primaryUser.timezone} />
            <Field label="Phone" value={primaryUser.phone} />
            <Field label="Zoom ID" value={primaryUser.zoom_id} />
            <Field label="Teams ID" value={primaryUser.teams_id} />
          </div>
          <div className="space-y-3">
            <Field label="Gender" value={primaryUser.gender} />
            <Field label="Nationality" value={primaryUser.nationality} />
            <Field label="Can Login" value={<Pill enabled={vendor.can_login} />} />
            <Field
              label="Invited At"
              value={
                vendor.invitedAt
                  ? new Date(vendor.invitedAt).toLocaleString()
                  : "—"
              }
            />
            <Field
              label="Last Activity At"
              value={
                vendor.lastActivityAt
                  ? new Date(vendor.lastActivityAt).toLocaleString()
                  : "—"
              }
            />
          </div>
        </Card>
      </section>

      <section>
        <SectionHeader title="Settings">
          {!readOnly && (
            <button onClick={onEditSettings} className={btnPrimary}>
              Update
            </button>
          )}
        </SectionHeader>

        <Card>
          <div className="space-y-3">
            <Field label="Client Manager" value="—" />
            <Field label="Default Legal Entity" value="—" />
            <Field label="Default Currency" value="—" />
            <Field label="Default Payment Method" value="—" />
          </div>
          <div className="space-y-3">
            <Field label="Default Payment Terms" value="—" />
            <Field label="Default CAT Log Conversion Scheme" value="—" />
            <Field label="Default Invoice PDF Template" value="—" />
          </div>
        </Card>
      </section>

      {isModalOpen && (
        <ConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to delete this vendor?"
          onCancel={() => setIsModalOpen(false)}
          onConfirm={handleConfirmDelete}
          confirmText="Yes, Delete"
          confirmColor="bg-red-600"
          confirmHoverColor="hover:bg-red-700"
        />
      )}
    </div>
  );
};

const Field = ({ label, value }) => {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div className="flex items-start text-sm">
      <span className="w-40 shrink-0 text-xs font-medium text-gray-500 pt-px">
        {label}:
      </span>
      <span className="text-gray-900">{isEmpty ? "—" : value}</span>
    </div>
  );
};

export default VendorView;
